'use client'

import type { ProductQuantityPickerProps } from '@maestro/commerce-blocks/components/product-quantity-picker/product-quantity-picker.types'

import classNames from 'classnames'
import { useTranslations } from 'next-intl'

import { useDomainType } from '@/hooks/use-domain-type'
import { useProductQuantityPicker } from '@maestro/commerce-blocks/components/product-quantity-picker/batons/use-product-quantity-picker'
import { QuantitySelector } from '@pearlconvert/ui/components/quantity-selector'

export default function ProductQuantityPicker(
  props: ProductQuantityPickerProps
) {
  const {
    id,
    quantity,
    maxQuantity,
    minQuantity: minQuantityProp = 1,
    onQuantityChange,
    className,
    messageClassName,
    controlSize,
    disabled = false,
  } = props
  const t = useTranslations('commerce-blocks')
  const domainType = useDomainType()
  // maxQuantity may be lower than the default minQuantity (e.g. 0 when out of stock)
  const minQuantity = Math.min(minQuantityProp, maxQuantity)
  const isDisabled = disabled || minQuantity === maxQuantity
  const { commitQuantity, showMessage } = useProductQuantityPicker({
    quantity,
    minQuantity,
    maxQuantity,
    onQuantityChange,
  })

  return (
    <QuantitySelector
      id={id}
      value={Math.min(quantity, maxQuantity)}
      minusDisabled={isDisabled || quantity <= minQuantity}
      plusDisabled={isDisabled || quantity >= maxQuantity}
      disabled={isDisabled}
      controlSize={controlSize}
      className={classNames('product-quantity-picker', className)}
      // Polar: the stock count is shown to retail only (POL-155).
      error={
        showMessage
          ? domainType === 'retail'
            ? t('cart.products-max-quantity', { count: maxQuantity })
            : t('cart.products-max-quantity-reached')
          : undefined
      }
      messageClassName={classNames(
        'product-quantity-picker__message',
        messageClassName
      )}
      quantityLabel={t('product.quantity')}
      decreaseLabel={t('product.decrease-qty')}
      increaseLabel={t('product.increase-qty')}
      testIdPrefix="product-quantity"
      blurFallback={minQuantity}
      onChange={commitQuantity}
    />
  )
}
